(() => {
  "use strict";

  const path = window.location.pathname;
  if (path !== "/a/search" && path !== "/a/search/") return;

  const bindingKey = "__sbProxySearchDrawerA11yBound";
  let ownsLock = false;
  let mobileDrawer = null;
  let focusGeneration = 0;
  const isMobile = () => window.matchMedia("(max-width: 991.98px)").matches;
  const isOpen = () => isMobile() && document.body.classList.contains("filter-toggle");
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  function resolveDrawer() {
    const root = document.getElementById("shopify-section-proxy-search");
    const shell = root?.querySelector("filter-component.plp-shell");
    const drawer = shell?.querySelector(".sidebar-section-content");
    const trigger = shell?.querySelector(".filter-btn .btn-filter");
    const closeButton = drawer?.querySelector(".btn-popup-close");
    if (!root || !shell || !drawer || !trigger || !closeButton) return null;

    root.dataset.proxyDrawerA11y = "bound";
    if (!drawer.id) drawer.id = "ProxySearchFilterDrawer";
    const title = drawer.querySelector(".sidebar-section-title");
    if (title) {
      if (!title.id) title.id = `${drawer.id}Title`;
      drawer.setAttribute("aria-labelledby", title.id);
    } else {
      const fallbackLabel = (
        trigger.getAttribute("aria-label") || trigger.textContent || ""
      ).trim();
      drawer.setAttribute("aria-label", fallbackLabel || "Filter and sort search results");
    }

    trigger.setAttribute("aria-controls", drawer.id);
    trigger.setAttribute("aria-haspopup", "dialog");
    if (!trigger.hasAttribute("aria-expanded")) {
      trigger.setAttribute("aria-expanded", "false");
    }
    return { root, drawer, trigger, closeButton, title };
  }

  const initialState = resolveDrawer();
  if (!initialState || window[bindingKey]) return;
  window[bindingKey] = true;

  function focusElement(element) {
    try {
      element?.focus({ preventScroll: true });
    } catch (_error) {
      element?.focus();
    }
  }

  function focusOpenedDrawer(state) {
    const generation = ++focusGeneration;
    let attempts = 0;
    const initialFocus = document.activeElement;
    const deadline = Date.now() + 500;
    function attempt() {
      if (generation !== focusGeneration || !isOpen() ||
        resolveDrawer()?.drawer !== state.drawer ||
        state.drawer.contains(document.activeElement) ||
        document.activeElement !== initialFocus) return;
      const close = state.closeButton;
      if (close.offsetWidth && close.offsetHeight &&
        window.getComputedStyle(close).visibility === "visible") focusElement(close);
      // Visibility changes with the drawer transition. Retry only while this
      // opening still owns focus, and stop as soon as focus enters the drawer.
      if (!state.drawer.contains(document.activeElement) && ++attempts < 120 && Date.now() < deadline) {
        if (window.requestAnimationFrame) window.requestAnimationFrame(attempt);
        else window.setTimeout(attempt, 16);
      }
    }
    attempt();
  }

  function focusableElements(state, includeActive = true) {
    return [...state.drawer.querySelectorAll(focusableSelector)].filter((element) => {
      return !element.hasAttribute("hidden") &&
        element.getAttribute("aria-hidden") !== "true" &&
        (element.offsetWidth && element.offsetHeight ||
          (includeActive && element === document.activeElement));
    });
  }

  function focusDesktopSidebar(state) {
    const target = focusableElements(state, false)[0] || state.title || state.drawer;
    if (!target.hasAttribute("tabindex") && (target === state.title || target === state.drawer)) {
      target.setAttribute("tabindex", "-1");
    }
    focusElement(target);
  }

  function acquirePopupLock() {
    if (document.body.classList.contains("show-popup")) return;
    document.body.classList.add("show-popup");
    ownsLock = true;
  }

  function releasePopupLock() {
    if (!ownsLock) return;
    document.body.classList.remove("show-popup");
    ownsLock = false;
  }

  function clearDialogState(state) {
    focusGeneration++;
    releasePopupLock();
    if (!state) return;
    state.drawer.removeAttribute("role");
    state.drawer.removeAttribute("aria-modal");
    state.trigger.setAttribute("aria-expanded", "false");
  }

  function closeDrawer(state, restoreFocus = false) {
    mobileDrawer = null;
    document.body.classList.remove("filter-toggle");
    clearDialogState(state);
    if (restoreFocus) focusElement(state?.trigger);
  }

  function syncDrawer(focusDrawer = false) {
    const state = resolveDrawer();
    if (!state) {
      if (!document.body.classList.contains("filter-toggle")) {
        mobileDrawer = null;
        releasePopupLock();
      }
      return;
    }
    if (!isMobile()) {
      const active = document.activeElement;
      const moveFocus = mobileDrawer === state.drawer &&
        (active === document.body || !active?.offsetWidth || !active?.offsetHeight);
      closeDrawer(state, false);
      if (moveFocus) focusDesktopSidebar(state);
      return;
    }
    if (!isOpen()) {
      mobileDrawer = null;
      clearDialogState(state);
      return;
    }

    mobileDrawer = state.drawer;
    acquirePopupLock();
    state.drawer.setAttribute("role", "dialog");
    state.drawer.setAttribute("aria-modal", "true");
    state.trigger.setAttribute("aria-expanded", "true");
    if (focusDrawer) focusOpenedDrawer(state);
  }

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const state = resolveDrawer();
    if (!state) return;

    const openControl = target.closest(".filter-btn .btn-filter");
    if (openControl === state.trigger && state.root.contains(openControl)) {
      window.setTimeout(() => syncDrawer(true), 0);
      return;
    }

    const closeControl = target.closest(
      ".btn-popup-close, .popup-layer, .jsFilterDrawerClose"
    );
    if (closeControl && state.root.contains(closeControl)) {
      window.setTimeout(() => closeDrawer(resolveDrawer(), true), 0);
    }
  });

  document.addEventListener("keydown", (event) => {
    if (!isOpen()) return;
    const state = resolveDrawer();
    if (!state) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closeDrawer(state, true);
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = focusableElements(state);
    if (!focusable.length) {
      event.preventDefault();
      focusElement(state.closeButton);
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (!state.drawer.contains(active)) {
      event.preventDefault();
      focusElement(first);
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      focusElement(last);
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      focusElement(first);
    }
  });

  window.addEventListener("resize", () => syncDrawer(false));
  window.addEventListener("pageshow", () => syncDrawer(false));
  window.addEventListener("sfs:results-updated", () => {
    syncDrawer(isOpen() && !resolveDrawer()?.drawer.contains(document.activeElement));
  });
  syncDrawer(isOpen());
})();
